import { compareDesc, parseISO } from 'date-fns';

import type { PermissionDeviation } from '@/userManagement/domain/entities/Permission';
import type { UserWireDto } from '@/userManagement/application/types/UserWireDto';
import {
  isCrossUserDetailShape,
  isCrossUserListItemShape,
  mapCrossUserPayloadToWireDto,
  parseInProgressApprovalRequestCount,
  parseApprovedApprovalRequestCount,
  parsePendingNewRoleId,
  parsePendingNewRoleName
} from '@/userManagement/application/mappers/crossUserListItemMapper';
import { isNestedUsernameDataBundle } from '@/userManagement/application/mappers/userListBundleGuards';
import { joinDistinctCustomIds, parseWireCustomId } from '@/userManagement/application/utils/parseWireCustomId';

function parseIsoDate(value: string | null): Date | null {
  if (value == null || String(value).trim() === '') return null;
  try {
    const d = parseISO(String(value));
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

function deviationDedupeKey(d: PermissionDeviation): string {
  return `${d.featureId}:${d.field}:${d.direction}`;
}

function appendOrderedUnique(seen: Set<string>, out: string[], value: string): void {
  const t = value.trim();
  if (t === '') return;
  const key = t.toLowerCase();
  if (seen.has(key)) return;
  seen.add(key);
  out.push(t);
}

function mergeDeviationsPreviews(previews: Array<PermissionDeviation[] | undefined>): PermissionDeviation[] | undefined {
  const merged: PermissionDeviation[] = [];
  const seen = new Set<string>();
  for (const list of previews) {
    if (!list?.length) continue;
    for (const d of list) {
      const k = deviationDedupeKey(d);
      if (seen.has(k)) continue;
      seen.add(k);
      merged.push(d);
    }
  }
  return merged.length > 0 ? merged : undefined;
}

/**
 * Sort shards for stable "primary" row: latest login first, then database name, then id.
 */
export function sortUserWireShardsForPrimarySelection(a: UserWireDto, b: UserWireDto): number {
  const da = parseIsoDate(a.lastLoginOn);
  const db = parseIsoDate(b.lastLoginOn);
  if (da && db) {
    const c = compareDesc(da, db);
    if (c !== 0) return c;
  } else if (da && !db) {
    return -1;
  } else if (!da && db) {
    return 1;
  }
  const dbA = a.assignedDatabases[0] ?? '';
  const dbB = b.assignedDatabases[0] ?? '';
  const dbCmp = dbA.localeCompare(dbB, undefined, { sensitivity: 'base' });
  if (dbCmp !== 0) return dbCmp;
  return String(a.id ?? '').localeCompare(String(b.id ?? ''));
}

/**
 * Collapses `{ username, data: UserShard[] }` into one {@link UserWireDto} per username.
 * Text fields: ordered unique values joined with ", ".
 * Booleans: true only if **every** shard is true. Failed attempts: sum across shards.
 * `id` is the cross-user id of the shard at index 0 after {@link sortUserWireShardsForPrimarySelection}.
 */
export function aggregateUsernameBundleToWireDto(username: string, shards: unknown[]): UserWireDto | null {
  const trimmedUsername = username.trim();
  const dtos: UserWireDto[] = [];
  for (const inner of shards) {
    const dto = mapBackendUserShardToWireDto(trimmedUsername, inner);
    if (dto) dtos.push(dto);
  }
  if (dtos.length === 0) return null;

  dtos.sort(sortUserWireShardsForPrimarySelection);
  const primary = dtos[0]!;

  const firstNamesSeen = new Set<string>();
  const firstNames: string[] = [];
  const lastNamesSeen = new Set<string>();
  const lastNames: string[] = [];
  const emailsSeen = new Set<string>();
  const emails: string[] = [];
  const phonesSeen = new Set<string>();
  const phones: string[] = [];
  const roleNamesSeen = new Set<string>();
  const roleNames: string[] = [];
  const databasesSeen = new Set<string>();
  const databasesOrdered: string[] = [];

  for (const d of dtos) {
    appendOrderedUnique(firstNamesSeen, firstNames, d.firstName);
    appendOrderedUnique(lastNamesSeen, lastNames, d.lastName);
    appendOrderedUnique(emailsSeen, emails, d.email);
    if (d.phone != null && d.phone.trim() !== '') {
      appendOrderedUnique(phonesSeen, phones, d.phone.trim());
    }
    const rn = d.permissionRoleName?.trim() ?? '';
    if (rn !== '') {
      const rk = rn.toLowerCase();
      if (!roleNamesSeen.has(rk)) {
        roleNamesSeen.add(rk);
        roleNames.push(rn);
      }
    }
    for (const db of d.assignedDatabases) {
      const t = db.trim();
      if (t === '') continue;
      const dk = t.toLowerCase();
      if (databasesSeen.has(dk)) continue;
      databasesSeen.add(dk);
      databasesOrdered.push(t);
    }
  }

  const isLocked = dtos.every((d) => d.isLocked);
  const isSuspended = dtos.every((d) => d.isSuspended);
  const isActive = dtos.every((d) => d.isActive ?? true);
  const forceNewPassword = dtos.every((d) => d.forceNewPassword);

  let failedSum = 0;
  for (const d of dtos) {
    const n = d.failedLoginAttempts ?? 0;
    failedSum += Number.isFinite(n) ? n : 0;
  }

  let lastLoginMax: string | null = null;
  let lastLoginMaxDate: Date | null = null;
  for (const d of dtos) {
    const parsed = parseIsoDate(d.lastLoginOn);
    if (!parsed) continue;
    if (!lastLoginMaxDate || compareDesc(parsed, lastLoginMaxDate) < 0) {
      lastLoginMaxDate = parsed;
      lastLoginMax = d.lastLoginOn;
    }
  }

  let utcMax: string = primary.utcUpdatedOn;
  let utcMaxDate = parseIsoDate(utcMax);
  for (const d of dtos) {
    const p = parseIsoDate(d.utcUpdatedOn);
    if (!p) continue;
    if (!utcMaxDate || compareDesc(p, utcMaxDate) < 0) {
      utcMaxDate = p;
      utcMax = d.utcUpdatedOn;
    }
  }

  const firstRoleId = dtos[0]?.permissionRoleId ?? null;
  const sameRoleId = firstRoleId != null && dtos.every((d) => d.permissionRoleId === firstRoleId) ? firstRoleId : null;

  const deviationsPreview = mergeDeviationsPreviews(dtos.map((d) => d.deviationsPreview));

  let inProgressApprovalRequestTotal = 0;
  let inProgressApprovalRequestSeen = false;
  for (const d of dtos) {
    if (d.inProgressApprovalRequestCount !== undefined) {
      inProgressApprovalRequestSeen = true;
      const n = d.inProgressApprovalRequestCount;
      if (typeof n === 'number' && Number.isFinite(n) && n > 0) {
        inProgressApprovalRequestTotal += Math.floor(n);
      }
    }
  }

  let approvedApprovalRequestTotal = 0;
  let approvedApprovalRequestSeen = false;
  for (const d of dtos) {
    if (d.approvedApprovalRequestCount !== undefined) {
      approvedApprovalRequestSeen = true;
      const n = d.approvedApprovalRequestCount;
      if (typeof n === 'number' && Number.isFinite(n) && n > 0) {
        approvedApprovalRequestTotal += Math.floor(n);
      }
    }
  }

  const firstNameJoined = firstNames.join(', ');
  const lastNameJoined = lastNames.join(', ');
  const emailJoined = emails.join(', ');
  const phoneJoined = phones.length > 0 ? phones.join(', ') : null;

  const permissionRoleNameJoined = roleNames.length === 0 ? null : roleNames.length === 1 ? roleNames[0]! : roleNames.join(', ');
  const customId = joinDistinctCustomIds(dtos.map((d) => d.customId));

  return {
    id: primary.id,
    username: primary.username,
    firstName: firstNameJoined,
    lastName: lastNameJoined,
    email: emailJoined,
    phone: phoneJoined,
    customId,
    permissionRoleId: sameRoleId,
    permissionRoleName: permissionRoleNameJoined,
    permissionRoleNames: roleNames.length > 0 ? roleNames : undefined,
    isLocked,
    isSuspended,
    isActive,
    forceNewPassword,
    failedLoginAttempts: failedSum,
    lastLoginOn: lastLoginMax,
    assignedDatabases: databasesOrdered,
    utcUpdatedOn: utcMax,
    deviationsPreview,
    ...(inProgressApprovalRequestSeen ? { inProgressApprovalRequestCount: inProgressApprovalRequestTotal } : {}),
    ...(approvedApprovalRequestSeen ? { approvedApprovalRequestCount: approvedApprovalRequestTotal } : {}),
    ...(() => {
      for (const d of dtos) {
        const fields = {
          ...(d.pendingNewRoleId !== undefined ? { pendingNewRoleId: d.pendingNewRoleId } : {}),
          ...(d.pendingNewRoleName !== undefined ? { pendingNewRoleName: d.pendingNewRoleName } : {})
        };
        if (fields.pendingNewRoleId !== undefined || fields.pendingNewRoleName !== undefined) {
          return fields;
        }
      }
      return {};
    })()
  };
}

export function mapBackendUserShardToWireDto(parentUsername: string, shard: unknown): UserWireDto | null {
  if (shard === null || typeof shard !== 'object') return null;
  const r = shard as Record<string, unknown>;
  const idRaw = r.id ?? r.crossUserId ?? r.cross_user_id;
  if (idRaw === null || idRaw === undefined || String(idRaw).trim() === '') return null;

  const usernameFromShard = typeof r.username === 'string' && r.username.trim() !== '' ? String(r.username).trim() : '';
  const username = usernameFromShard || parentUsername;

  const roleIdRaw = r.permissionRoleId ?? r.permission_role_id;
  let permissionRoleId: number | null = null;
  if (typeof roleIdRaw === 'number' && Number.isFinite(roleIdRaw)) {
    permissionRoleId = roleIdRaw;
  } else if (roleIdRaw != null && roleIdRaw !== '') {
    const n = parseInt(String(roleIdRaw), 10);
    permissionRoleId = Number.isNaN(n) ? null : n;
  }

  const tenantDb = r.tenantDatabaseName != null && String(r.tenantDatabaseName).trim() !== '' ? String(r.tenantDatabaseName).trim() : '';
  const assignedFromShard = r.assignedDatabases ?? r.assigned_databases ?? r.databases;
  let assignedDatabases: string[] = [];
  if (Array.isArray(assignedFromShard)) {
    assignedDatabases = assignedFromShard.map((x) => String(x)).filter(Boolean);
  } else if (tenantDb) {
    assignedDatabases = [tenantDb];
  }

  const primaryEmail = r.primaryEmail ?? r.email;
  const email = primaryEmail != null && String(primaryEmail).trim() !== '' ? String(primaryEmail).trim() : '';

  const phoneRaw = r.primaryPhone ?? r.phone ?? r.primary_phone;
  const phone = phoneRaw != null && String(phoneRaw).trim() !== '' ? String(phoneRaw).trim() : null;

  const pendingShard = parseInProgressApprovalRequestCount(r);
  const approvedShard = parseApprovedApprovalRequestCount(r);
  const pendingNewRoleId = parsePendingNewRoleId(r);
  const pendingNewRoleName = parsePendingNewRoleName(r);
  const hasPendingRole =
    (pendingNewRoleId !== undefined && pendingNewRoleId !== null) ||
    (pendingNewRoleName !== undefined && pendingNewRoleName !== null && pendingNewRoleName !== '');

  return {
    id: String(idRaw).trim(),
    username,
    firstName: String(r.firstName ?? r.first_name ?? ''),
    lastName: String(r.lastName ?? r.last_name ?? ''),
    email,
    phone,
    customId: parseWireCustomId(r),
    permissionRoleId,
    permissionRoleName: (r.roleName ?? r.permissionRoleName ?? r.permission_role_name ?? null) as string | null,
    isLocked: Boolean(r.isLocked ?? r.is_locked),
    isSuspended: Boolean(r.isSuspended ?? r.is_suspended),
    isActive: (r.isActive ?? r.is_active) !== false,
    forceNewPassword: Boolean(r.forceNewPassword ?? r.force_new_password),
    failedLoginAttempts: Number(r.failedAttempts ?? r.failedLoginAttempts ?? r.failed_login_attempts ?? 0),
    lastLoginOn: (r.lastLoginOn ?? r.last_login_on ?? null) as string | null,
    assignedDatabases,
    utcUpdatedOn: String(r.utcUpdatedOn ?? r.utc_updated_on ?? new Date().toISOString()),
    deviationsPreview: r.deviationsPreview as UserWireDto['deviationsPreview'],
    ...(pendingShard !== undefined ? { inProgressApprovalRequestCount: pendingShard } : {}),
    ...(approvedShard !== undefined ? { approvedApprovalRequestCount: approvedShard } : {}),
    ...(hasPendingRole
      ? {
          ...(pendingNewRoleId !== undefined ? { pendingNewRoleId } : {}),
          ...(pendingNewRoleName !== undefined ? { pendingNewRoleName } : {})
        }
      : {})
  };
}

/**
 * Normalizes backend user list `items`: flat rows stay 1:1; `{ username, data: [...] }` becomes **one** row per username.
 */
export function normalizeUserListWireRows(rawItems: unknown[]): UserWireDto[] {
  const out: UserWireDto[] = [];
  for (const row of rawItems) {
    if (isNestedUsernameDataBundle(row)) {
      const dto = aggregateUsernameBundleToWireDto(row.username, row.data);
      if (dto) out.push(dto);
      continue;
    }
    if (isCrossUserDetailShape(row) || isCrossUserListItemShape(row)) {
      const dto = mapCrossUserPayloadToWireDto(row);
      if (dto) out.push(dto);
      continue;
    }
    const parentUsername =
      typeof (row as Record<string, unknown>).username === 'string' ? String((row as Record<string, unknown>).username) : '';
    const dto = mapBackendUserShardToWireDto(parentUsername, row);
    if (dto) out.push(dto);
  }
  return out;
}
